/**
 * EDGE UTILITIES
 *
 * Handles edge styling, adjacency list construction, and collision-aware
 * edge path routing with label placement.
 */

import { MarkerType } from 'reactflow';

import {
  NODE_WIDTH,
  NODE_MIN_HEIGHT,
  EDGE_TO_NODE_GAP,
  MIN_EDGE_TO_EDGE_GAP,
  MIN_ROUTING_ZONE_HEIGHT,
  MIN_CHILD_BELOW_PARENT,
  FORCED_CHILD_BELOW_OFFSET,
  EFFECTIVE_CHILD_TOP_BUFFER,
  LABEL_HALF_HEIGHT,
  LABEL_HEIGHT,
  MIN_LABEL_WIDTH,
  LABEL_VERTICAL_LINE_OFFSET,
  ARROW_SIZE,
  VERTICAL_ALIGN_THRESHOLD,
  NODE_MAX_HEIGHT,
} from '../../../../Config/ExistingVisaTreeViewConsts.js';



// ── Adjacency List Construction ─────────────────────────────────────────

/**
 * Convert raw edges into a parent -> ordered children map.
 * Orders children as YES (left), neutral (middle), NO (right).
 *
 * @param {Object} nodes - Node map
 * @param {Object} edges - Edge map
 * @returns {Object} { [parentId]: [{ edge, node }] }
 */
export const buildAdjacencyList = (nodes, edges) => {
  const children = {};
  const valid = new Set(Object.keys(nodes).map(String));

  for (const eid in edges) {
    const e = edges[eid];
    const pid = String(e.parent_node_id);
    const cid = String(e.child_node_id);

    if (!valid.has(cid) || !valid.has(pid)) continue;

    const child = nodes[cid] || nodes[Number(cid)];
    if (!child) continue;

    if (!children[pid]) children[pid] = [];

    const lower = (e.text || '').toLowerCase();
    children[pid].push({
      edge: e,
      node: { ...child, id: cid },
      _sort: lower.includes('yes') ? 0 : lower.includes('no') ? 2 : 1,
    });
  }

  for (const pid in children) {
    children[pid].sort((a, b) =>
      a._sort !== b._sort
        ? a._sort - b._sort
        : String(a.edge.id).localeCompare(String(b.edge.id))
    );
  }

  return children;
};


// ── Edge Path Routing ───────────────────────────────────────────────────

/**
 * Calculate routed edge paths and collision-free label positions.
 *
 * @param {Object} positions - Node positions by id
 * @param {Object} nodeHeights - Node heights by id
 * @param {Object} children - Parent-child adjacency map
 * @returns {Array} Edge path objects for rendering
 */
export const calculateEdgePaths = (positions, nodeHeights, children) => {
  const edgePaths = [];
  const placedLabels = [];
  const nodeBounds = buildNodeBounds(positions, nodeHeights);

  for (const pid in children) {
    const parentPos = positions[pid];
    if (!parentPos) continue;
    
    const parentH = nodeHeights[pid];
    const kids = children[pid];
    if (!kids || kids.length === 0) continue;

    const validChildren = collectRenderableChildren(kids, positions);
    if (validChildren.length === 0) continue;

    const parentBot = parentPos.y + parentH;
    const parentCX = parentPos.x + NODE_WIDTH / 2;

    const routingZone = calculateRoutingZone(parentPos, parentBot, validChildren);
    const childCount = validChildren.length;

    for (let i = 0; i < childCount; i++) {
      const child = validChildren[i];
      const childId = child.node.id;
      const childPos = child.position;
      const childCX = childPos.x + NODE_WIDTH / 2;
      const childTop = childPos.y;
      const effectiveChildTop = Math.max(childTop, parentBot + EFFECTIVE_CHILD_TOP_BUFFER);

      let chY = calculateStepEdge(i, childCount, routingZone, parentBot, effectiveChildTop);

      const vertical = Math.abs(parentCX - childCX) < VERTICAL_ALIGN_THRESHOLD;

      if (!vertical) {
        chY = shiftChannelClearOfNodes(chY, parentBot, effectiveChildTop, parentCX, childCX, nodeBounds);
        chY = avoidParallelEdgeOverlap(chY, parentCX, childCX, effectiveChildTop, edgePaths);
      }

      const eText = child.edge.text || '';
      const labelWidth = Math.max(MIN_LABEL_WIDTH, eText.length * 7 + 20);
      const labelHalfWidth = labelWidth / 2;
      const labelOffset = labelHalfWidth + LABEL_VERTICAL_LINE_OFFSET;

      const isValid = (x, y) =>
        !hitsNode(x, y, labelHalfWidth, nodeBounds) &&
        !hitsLabel(x, y, labelHalfWidth, placedLabels) &&
        !hitsOtherEdgePath(x, y, labelHalfWidth, child.edge.id, edgePaths);

      let path;

      if (vertical) {
        path = `M ${parentCX} ${parentBot} L ${childCX} ${childTop}`;
      } else {
        path = `M ${parentCX} ${parentBot} L ${parentCX} ${chY} L ${childCX} ${chY} L ${childCX} ${childTop}`;
      }

      const fallbackLabel = {
        x: vertical ? parentCX + labelOffset : (parentCX + childCX) / 2,
        y: vertical ? parentBot + (childTop - parentBot) / 2 : chY,
      };

      // Walk each segment of the path in priority order, return first clear spot
      const label =
        (vertical
          ? findLabelOnSegment(parentCX, parentBot, childCX, childTop, labelHalfWidth, isValid)
          : findLabelOnSegment(parentCX, chY, childCX, chY, labelHalfWidth, isValid) ??
            findLabelOnSegment(parentCX, parentBot, parentCX, chY, labelHalfWidth, isValid) ??
            findLabelOnSegment(childCX, chY, childCX, childTop, labelHalfWidth, isValid)) ??
        fallbackLabel;

      placedLabels.push({
        l: label.x - labelHalfWidth,
        r: label.x + labelHalfWidth,
        t: label.y - LABEL_HALF_HEIGHT,
        b: label.y + LABEL_HALF_HEIGHT,
      });

      edgePaths.push({
        id: child.edge.id,
        path,
        text: eText,
        color: getEdgeColor(eText),
        labelX: label.x,
        labelY: label.y,
        arrowX: childCX,
        arrowY: childTop,
        startX: parentCX,
        startY: parentBot,
        endX: childCX,
        endY: childTop,
        parentId: pid,
        childId,
        channelY: chY,
      });
    }
  }

  return mapEdgePathsToReactFlowEdges(edgePaths);
};

function formatEdgeLabel(text) {
  const label = text || '';
  const isYes = label.toLowerCase().includes('yes');
  const isNo = label.toLowerCase().includes('no');
  const icon = isYes ? '✓' : isNo ? '✗' : '';
  return label ? `${icon ? icon + ' ' : ''}${label}` : undefined;
}

function mapEdgePathsToReactFlowEdges(edgePaths) {
  return edgePaths.map((edgePath) => ({
    id: String(edgePath.id),
    source: String(edgePath.parentId),
    target: String(edgePath.childId),
    type: 'customEdge',
    style: { stroke: edgePath.color, strokeWidth: 2 },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: edgePath.color,
      width: ARROW_SIZE,
      height: ARROW_SIZE,
    },
    data: {
      path: edgePath.path,
      label: formatEdgeLabel(edgePath.text),
      labelX: edgePath.labelX,
      labelY: edgePath.labelY,
      color: edgePath.color,
    },
  }));
}

// ── Geometry Helpers ────────────────────────────────────────────────────

const LABEL_CANDIDATE_STEPS = [0.5, 0.4, 0.6, 0.3, 0.7, 0.2, 0.8];
const CHANNEL_SEARCH_STEP = 8;
const CHANNEL_SEARCH_LIMIT = 300;
const MAX_CHANNEL_SHIFT_ATTEMPTS = 30;
const NODE_BOUND_PADDING = 4;

const buildNodeBounds = (positions, nodeHeights) => {
  const bounds = [];

  for (const id in positions) {
    const p = positions[id];
    bounds.push({
      left: p.x - NODE_BOUND_PADDING,
      right: p.x + NODE_WIDTH + NODE_BOUND_PADDING,
      top: p.y - NODE_BOUND_PADDING,
      bottom: p.y + NODE_BOUND_PADDING + (nodeHeights[id] || NODE_MIN_HEIGHT),
    });
  }

  return bounds;
};

const horizontalSegmentsOverlap = (y1, x1Min, x1Max, y2, x2Min, x2Max) => {
  const yTolerance = 8;
  if (Math.abs(y1 - y2) > yTolerance) return false;
  return !(x1Max < x2Min || x2Max < x1Min);
};

const checkEdgePathOverlap = (newEdge, placedEdges) => {
  if (newEdge.startX === newEdge.endX) return false;

  for (const prevEdge of placedEdges) {
    if (prevEdge.startX === prevEdge.endX) continue;

    const newXMin = Math.min(newEdge.startX, newEdge.endX);
    const newXMax = Math.max(newEdge.startX, newEdge.endX);
    const prevXMin = Math.min(prevEdge.startX, prevEdge.endX);
    const prevXMax = Math.max(prevEdge.startX, prevEdge.endX);

    if (
      horizontalSegmentsOverlap(
        newEdge.channelY,
        newXMin,
        newXMax,
        prevEdge.channelY,
        prevXMin,
        prevXMax
      )
    ) {
      return true;
    }
  }

  return false;
};

const segmentHitsBoundingBox = (x1, y1, x2, y2, lx, rx, ty, by) => {
  const segMinX = Math.min(x1, x2);
  const segMaxX = Math.max(x1, x2);
  const segMinY = Math.min(y1, y2);
  const segMaxY = Math.max(y1, y2);

  return !(segMaxX < lx || segMinX > rx || segMaxY < ty || segMinY > by);
};

const hSegHitsNode = (y, x1, x2, nodeBounds) => {
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);

  for (const n of nodeBounds) {
    if (y >= n.top && y <= n.bottom && right > n.left && left < n.right) {
      return true;
    }
  }

  return false;
};

const shiftChannelClearOfNodes = (baseY, parentBot, childTop, x1, x2, nodeBounds) => {
  if (!hSegHitsNode(baseY, x1, x2, nodeBounds)) return baseY;

  const minY = parentBot + EDGE_TO_NODE_GAP;
  const maxY = childTop - ARROW_SIZE - EDGE_TO_NODE_GAP;
  for (
    let offset = CHANNEL_SEARCH_STEP;
    offset < CHANNEL_SEARCH_LIMIT;
    offset += CHANNEL_SEARCH_STEP
  ) {
    const upY = baseY - offset;
    if (upY >= minY && !hSegHitsNode(upY, x1, x2, nodeBounds)) return upY;

    const downY = baseY + offset;
    if (downY <= maxY && !hSegHitsNode(downY, x1, x2, nodeBounds)) return downY;
  }

  return baseY;
};

const collectRenderableChildren = (kids, positions) => {
  const result = [];

  for (const child of kids) {
    const childPos = positions[child.node.id];
    if (!childPos) continue;

    result.push({
      edge: child.edge,
      node: child.node,
      position: childPos,
    });
  }

  return result;
};

const calculateRoutingZone = (parentPos, parentBottom, validChildren) => {
  let minChildY = parentPos.y + NODE_MAX_HEIGHT;
  for (const child of validChildren) {
    if (child.position.y < minChildY) minChildY = child.position.y;
  }

  if (minChildY <= parentBottom + MIN_CHILD_BELOW_PARENT) {
    minChildY = parentBottom + FORCED_CHILD_BELOW_OFFSET;
  }

  const top = parentBottom + EDGE_TO_NODE_GAP;
  const height = Math.max(
    MIN_ROUTING_ZONE_HEIGHT,
    minChildY - ARROW_SIZE - EDGE_TO_NODE_GAP - top
  );

  return { top, height };
};

const calculateStepEdge = (
  childIndex,
  childCount,
  routingZone,
  parentBottom,
  effectiveChildTop
) => {
  const evenlySpacedChannelY =
    childCount === 1
      ? routingZone.top + routingZone.height * 0.5
      : routingZone.top +
        Math.max(MIN_EDGE_TO_EDGE_GAP, routingZone.height / (childCount + 1)) *
          (childIndex + 1);

  return Math.max(
    parentBottom + EDGE_TO_NODE_GAP,
    Math.min(
      evenlySpacedChannelY,
      effectiveChildTop - ARROW_SIZE - EDGE_TO_NODE_GAP
    )
  );
};

const avoidParallelEdgeOverlap = (
  channelY,
  parentCenterX,
  childCenterX,
  effectiveChildTop,
  edgePaths
) => {
  let adjustedChannelY = channelY;
  let attempts = 0;

  while (
    checkEdgePathOverlap(
      { startX: parentCenterX, endX: childCenterX, channelY: adjustedChannelY },
      edgePaths
    ) &&
    attempts < MAX_CHANNEL_SHIFT_ATTEMPTS
  ) {
    adjustedChannelY += MIN_EDGE_TO_EDGE_GAP;

    if (adjustedChannelY > effectiveChildTop - ARROW_SIZE - EDGE_TO_NODE_GAP) {
      adjustedChannelY -= MIN_EDGE_TO_EDGE_GAP;
      break;
    }

    attempts++;
  }

  return adjustedChannelY;
};

/**
 * Walk along a line segment and return the first position where a label fits.
 *
 * Horizontal segments try: on the line → shifted left/right → above/below.
 * Vertical segments try: left and right of the line (can't place on the line itself).
 * Returns null if no clear spot is found on this segment.
 *
 * @param {number} x1, y1 - Segment start
 * @param {number} x2, y2 - Segment end
 * @param {number} hw - Label half-width
 * @param {Function} isValid - (x, y) => boolean — true if position is clear
 * @returns {{ x, y } | null}
 */
const findLabelOnSegment = (x1, y1, x2, y2, hw, isValid) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const isHorizontal = Math.abs(dx) >= Math.abs(dy);
  const sideOff = hw + LABEL_VERTICAL_LINE_OFFSET;

  // Vertical segments that are too short won't have room for a label
  if (!isHorizontal && Math.abs(dy) <= LABEL_HEIGHT * 2) return null;

  for (const t of LABEL_CANDIDATE_STEPS) {
    const mx = x1 + dx * t;
    const my = y1 + dy * t;

    if (isHorizontal) {
      const verticalOff = LABEL_HALF_HEIGHT + 12;
      if (isValid(mx, my))                  return { x: mx,              y: my };
      if (isValid(mx - sideOff, my))     return { x: mx - sideOff, y: my };
      if (isValid(mx + sideOff, my))     return { x: mx + sideOff, y: my };
      if (isValid(mx, my - verticalOff))    return { x: mx, y: my - verticalOff };
      if (isValid(mx, my + verticalOff))    return { x: mx, y: my + verticalOff };
    } else {
      if (isValid(mx + sideOff, my)) return { x: mx + sideOff, y: my };
      if (isValid(mx - sideOff, my)) return { x: mx - sideOff, y: my };
    }
  }

  return null;
};

const hitsNode = (x, y, hw, nodeBounds) => {
  const l = x - hw;
  const r = x + hw;
  const t = y - LABEL_HALF_HEIGHT;
  const b = y + LABEL_HALF_HEIGHT;

  for (const n of nodeBounds) {
    if (r > n.left && l < n.right && b > n.top && t < n.bottom) return true;
  }

  return false;
};

const hitsLabel = (x, y, hw, placedLabels) => {
  const l = x - hw;
  const r = x + hw;
  const t = y - LABEL_HALF_HEIGHT;
  const b = y + LABEL_HALF_HEIGHT;

  for (const p of placedLabels) {
    if (l < p.r && r > p.l && t < p.b && b > p.t) return true;
  }

  return false;
};

const hitsOtherEdgePath = (x, y, hw, currentEdgeId, edgePaths) => {
  const lx = x - hw;
  const rx = x + hw;
  const ty = y - LABEL_HALF_HEIGHT;
  const by = y + LABEL_HALF_HEIGHT;

  for (const edge of edgePaths) {
    if (edge.id === currentEdgeId) continue;

    const { startX, startY, endX, endY, channelY } = edge;
    if (startX === endX) {
      if (segmentHitsBoundingBox(startX, startY, endX, endY, lx, rx, ty, by)) {
        return true;
      }
    } else {
      if (
        segmentHitsBoundingBox(startX, startY, startX, channelY, lx, rx, ty, by)
      ) {
        return true;
      }

      if (
        segmentHitsBoundingBox(startX, channelY, endX, channelY, lx, rx, ty, by)
      ) {
        return true;
      }

      if (segmentHitsBoundingBox(endX, channelY, endX, endY, lx, rx, ty, by)) {
        return true;
      }
    }
  }

  return false;
};

// ── Edge Styling ────────────────────────────────────────────────────────

/**
 * Get edge color based on label text semantics.
 *
 * @param {string} text - Edge label text
 * @returns {string} Hex color
 */
export const getEdgeColor = (text) => {
  const l = (text || '').toLowerCase();
  if (l.includes('yes')) return '#078346';
  if (l.includes('no')) return '#dc2626';
  return '#6b7280';
};